// Documentación pública (/docs). Vive dentro del frontend actual en vez de en un sitio aparte:
// montar un Next.js/Nextra solo para esto añadiría un tercer stack, su build y su contenedor a un
// proyecto que ya despliega CRA + Docker. Aquí se despliega con el mismo push que todo lo demás.
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Search, Menu, X, ChevronRight, ChevronLeft, ArrowLeft, LifeBuoy } from "lucide-react";
import { SECCIONES, PAGINAS, buscarPaginas } from "../docs";

function Buscador({ onIr }) {
  const [q, setQ] = useState("");
  const res = useMemo(() => buscarPaginas(q), [q]);
  return (
    <div className="relative">
      <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        value={q} onChange={(e) => setQ(e.target.value)} data-testid="docs-search"
        placeholder="Buscar en la documentación…"
        className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
      />
      {res.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          {res.map((p) => (
            <button key={`${p.seccion}/${p.id}`} onClick={() => { setQ(""); onIr(p); }}
              className="block w-full px-3 py-2 text-left hover:bg-slate-50">
              <div className="text-sm font-medium text-slate-800">{p.titulo}</div>
              <div className="text-[11px] text-slate-400">{p.seccionTitulo}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Nav({ actual, onIr }) {
  return (
    <nav className="space-y-6">
      {SECCIONES.map((s) => (
        <div key={s.id}>
          <div className="mb-2 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">{s.titulo}</div>
          <ul className="space-y-0.5">
            {s.paginas.map((p) => {
              const activa = actual?.seccion === s.id && actual?.id === p.id;
              return (
                <li key={p.id}>
                  <button onClick={() => onIr({ ...p, seccion: s.id })}
                    data-testid={`docs-nav-${p.id}`}
                    className={`block w-full rounded-lg px-3 py-1.5 text-left text-sm transition-colors ${
                      activa ? "bg-blue-50 font-semibold text-blue-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                    {p.titulo}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export default function Docs() {
  const { seccion, pagina } = useParams();
  const navigate = useNavigate();
  const [menuAbierto, setMenuAbierto] = useState(false);

  // Sin ruta concreta se entra por la primera página, que es la portada.
  const idx = useMemo(() => {
    const i = PAGINAS.findIndex((p) => p.seccion === seccion && p.id === pagina);
    return i >= 0 ? i : 0;
  }, [seccion, pagina]);
  const actual = PAGINAS[idx];
  const anterior = idx > 0 ? PAGINAS[idx - 1] : null;
  const siguiente = idx < PAGINAS.length - 1 ? PAGINAS[idx + 1] : null;

  const ir = (p) => {
    setMenuAbierto(false);
    navigate(`/docs/${p.seccion}/${p.id}`);
  };

  // Al cambiar de página se vuelve arriba: sin esto se aterriza a media página, que despista.
  useEffect(() => { window.scrollTo(0, 0); }, [idx]);

  // Ruta desconocida o /docs a secas: se normaliza a la portada para que la URL sea compartible.
  useEffect(() => {
    if (!seccion || !pagina || PAGINAS.findIndex((p) => p.seccion === seccion && p.id === pagina) < 0) {
      navigate(`/docs/${PAGINAS[0].seccion}/${PAGINAS[0].id}`, { replace: true });
    }
  }, [seccion, pagina, navigate]);

  const Contenido = actual.Comp;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6">
          <button onClick={() => setMenuAbierto((v) => !v)} data-testid="docs-menu-toggle"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden">
            {menuAbierto ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link to="/"><img src="/ytmethod_transparent.png" alt="YTMethod" className="h-6 w-auto" /></Link>
          <span className="hidden text-sm text-slate-400 sm:inline">Documentación</span>
          <div className="ml-auto hidden w-80 lg:block"><Buscador onIr={ir} /></div>
          <Link to="/app" className="ml-auto shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 lg:ml-0">
            Ir a la app
          </Link>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-10 px-4 sm:px-6">
        <aside className={`${menuAbierto ? "block" : "hidden"} lg:block w-full shrink-0 py-6 lg:w-64 lg:py-10`}>
          <div className="mb-6 lg:hidden"><Buscador onIr={ir} /></div>
          <div className="lg:sticky lg:top-24"><Nav actual={actual} onIr={ir} /></div>
        </aside>

        <main className={`${menuAbierto ? "hidden" : "block"} min-w-0 flex-1 py-8 lg:block lg:py-12`}>
          <article className="max-w-3xl">
            <div className="mb-2 text-sm font-semibold text-blue-600">{actual.seccionTitulo}</div>
            <h1 className="mb-8 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{actual.titulo}</h1>
            <Contenido />
          </article>

          <div className="mt-16 max-w-3xl border-t border-slate-200 pt-6">
            <div className="flex flex-wrap gap-3">
              {anterior && (
                <button onClick={() => ir(anterior)} data-testid="docs-prev"
                  className="flex flex-1 items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-left hover:border-slate-300 hover:bg-slate-50">
                  <ChevronLeft size={18} className="shrink-0 text-slate-400" />
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-wide text-slate-400">Anterior</span>
                    <span className="block truncate text-sm font-semibold text-slate-800">{anterior.titulo}</span>
                  </span>
                </button>
              )}
              {siguiente && (
                <button onClick={() => ir(siguiente)} data-testid="docs-next"
                  className="flex flex-1 items-center justify-end gap-2 rounded-xl border border-slate-200 px-4 py-3 text-right hover:border-slate-300 hover:bg-slate-50">
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-wide text-slate-400">Siguiente</span>
                    <span className="block truncate text-sm font-semibold text-slate-800">{siguiente.titulo}</span>
                  </span>
                  <ChevronRight size={18} className="shrink-0 text-slate-400" />
                </button>
              )}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <Link to="/" className="inline-flex items-center gap-1.5 hover:text-slate-800">
                <ArrowLeft size={15} /> Volver al inicio
              </Link>
              <Link to="/ayuda" className="inline-flex items-center gap-1.5 hover:text-slate-800">
                <LifeBuoy size={15} /> ¿No lo resuelve? Escríbenos
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
